import { NotificationResponse } from 'api/actor';
import {
  useSignalRNotifications,
  SignalRNotificationsConfig
} from './useSignalRNotifications';

/**
 * DTO pour les notifications de progression du processus borrower
 */
export interface BorrowerProcessProgressDto {
  notificationPopIn?: NotificationResponse;
}

/**
 * Types d'étapes du processus borrower
 */
export enum BorrowerProcessStepType {
  SubscriptionRequest = 'subscriptionRequest',
  MedicalSurvey = 'medicalSurvey',
  ApprovedAgreement = 'approvedAgreement',
  SubscriptionCertificate = 'subscriptionCertificate'
}

/**
 * Statuts possibles du processus
 */
export enum BorrowerProcessStatus {
  Pending = 'pending',
  Sent = 'sent',
  Completed = 'completed',
  Signed = 'signed'
}

/**
 * Configuration pour les notifications du processus borrower
 */
export const BORROWER_PROCESS_NOTIFICATIONS_CONFIG: Omit<
  SignalRNotificationsConfig<BorrowerProcessProgressDto>,
  'groupId'
> = {
  route: '/signalr/borrower-process',
  joinMethod: 'JoinDevisGroup',
  leaveMethod: 'LeaveDevisGroup',
  eventName: 'ProcessProgress'
};

/**
 * Hook pour les notifications du processus borrower via SignalR
 *
 * @param quoteId - ID du devis pour rejoindre le groupe de notifications
 *
 * @example
 * ```tsx
 * const { notifications, isConnected, memoizedConnect, memoizedDisconnect } =
 *   useBorrowerProcessNotifications(quoteId);
 *
 * useEffect(() => {
 *   memoizedConnect();
 *   return memoizedDisconnect;
 * }, [memoizedConnect, memoizedDisconnect]);
 *
 * // Écouter les mises à jour
 * useEffect(() => {
 *   if (latestNotification) {
 *     console.log('Process update:', latestNotification);
 *     refreshData();
 *   }
 * }, [latestNotification]);
 * ```
 */
export const useBorrowerProcessNotifications = (quoteId?: string) => {
  const {
    connection,
    isConnected,
    error,
    memoizedConnect,
    memoizedDisconnect,
    notifications,
    latestNotification,
    clearNotifications
  } = useSignalRNotifications<BorrowerProcessProgressDto>({
    ...BORROWER_PROCESS_NOTIFICATIONS_CONFIG,
    groupId: quoteId
  });

  return {
    // Connection state
    connection,
    isConnected,
    error,

    // Connection methods
    memoizedConnect,
    memoizedDisconnect,

    // Notification data
    notifications,
    latestNotification,

    // Utility methods
    clearNotifications
  };
};
